import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { DestroyRef, importProvidersFrom, ModuleWithProviders, NgModule, NgZone } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, PreloadAllModules, Router, RouterModule, Routes } from '@angular/router';
import { NavigationActionTiming, RouterState, StoreRouterConnectingModule } from '@ngrx/router-store';
import { AuthComponent } from './auth/auth.component';
import { LoginComponent } from './features/login/login.component';
import { AuthGuard } from './guards/auth.guard';
import { BrowserBlockerComponent } from './guards/browser-guard/browser-blocker.component';
import { BrowserGuard } from './guards/browser-guard/browser.guard';
import { CanDeactivateGuard } from './guards/can-deactivate.guard';
import { InvoluntaryChangeGuard } from './guards/involuntary-change.guard';
import { LoggedInUserGuard } from './guards/logged-in-user.guard';
import { MileagePlanConfirmationGuard } from './guards/mileage-plan-enrollment-confirmation.guard';
import { ManualAssociationGuard } from './guards/manual-association.guard';
import { PaymentDueGuard } from './guards/payment-due.guard';
import { PaymentGuard } from './guards/payment.guard';
import { ReservationGuard } from './guards/reservation.guard';
import { SeatMapGuard } from './guards/seatmap.guard';
import { ApiInterceptor } from './interceptors/api.interceptor';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { EventInterceptor } from './interceptors/event.interceptor';
import { CommonHeaderLayoutComponent } from './shared/ux-header/common-header-layout/common-header-layout.component';
import { NoCommonHeaderLayoutComponent } from './shared/ux-header/no-common-header-layout/no-common-header-layout.component';
import { UXModule } from './shared/ux/ux.module';
import { TicketDetailsGuard } from './guards/ticket-details.guard';
import { BaggageJourneyGuard } from './guards/baggage-journey.guard';
import { Store, StoreModule } from '@ngrx/store';
import { ssrMetricsFeatureKey } from './features/ssr/state/ssr-metrics.state';
import { ssrMetricsReducer } from './features/ssr/state/ssr-metrics.reducer';
import { distinctUntilChanged, filter } from 'rxjs';
import { RootState } from './state/state';
import { getUser } from './services/login-service/state/login-service.selector';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface DynatraceRumApi {
  enableManualPageDetection(): void;
  identifyUser(value: string): void;
  sendSessionProperties(javaLongOrObject?: unknown, date?: unknown, shortString?: Record<string, string>, javaDouble?: unknown): unknown;
  setPage(page: { name: string; group: string }): void;
}

const routes: Routes = [
  {
    path: 'browser-blocker',
    component: BrowserBlockerComponent, // no guards because this route is for incompatible browsers, not logged in, not authorized
  },
  {
    path: 'auth',
    component: AuthComponent,
    canActivate: [AuthGuard], // no login guard because you can view this route without being logged in
  },
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard], // should be included in all routes
  },
  {
    // if you do this, you don't need multiple router outlets
    path: '',
    component: CommonHeaderLayoutComponent,
    children: [
      {
        path: 'home',
        loadChildren: () => import('./features/home/home.routes').then((m) => m.routes),
        providers: [MileagePlanConfirmationGuard],
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard], // should be included in all routes
      },
      {
        path: 'reservation',
        loadChildren: () => import('./features/reservation/reservation.routes').then((m) => m.routes),
        providers: [
          PaymentDueGuard,
          PaymentGuard,
          SeatMapGuard,
          importProvidersFrom(StoreModule.forFeature(ssrMetricsFeatureKey, ssrMetricsReducer)),
        ],
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
        canActivateChild: [ReservationGuard],
      },
      {
        path: 'search',
        loadChildren: () => import('./features/search/search.routes').then((m) => m.routes),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
        data: { menuId: 'reservation-search' },
      },
      {
        path: 'coupon-record-search',
        loadChildren: () => import('./features/coupon-record-search/coupon-record-search.routes').then((m) => m.routes),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
        data: { menuId: 'ticket-search' },
      },
      {
        path: 'mileage-plan-profile-search',
        loadChildren: () => import('./features/mileage-plan-profile-search/mileage-plan-profile-search.routes').then((m) => m.routes),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
        data: { menuId: 'guest-profile-search' },
      },
      {
        path: 'book-flights',
        loadChildren: () => import('./features/initial-booking/initial-booking.module').then((m) => m.InitialBookingModule),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
        data: { menuId: 'book-flights' },
      },
      {
        path: 'home/baggage-journey',
        loadChildren: () => import('./features/baggage-journey/baggage-journey.routes').then((m) => m.BaggageJourneyRoutes),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
        canActivateChild: [BaggageJourneyGuard],
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'home',
      },
    ],
  },
  {
    path: '',
    component: NoCommonHeaderLayoutComponent,
    children: [
      {
        path: 'manual-association',
        loadChildren: () => import('./features/manual-association/manual-association.routes').then((m) => m.ManualAssociationRoutes),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
        canActivateChild: [ManualAssociationGuard],
      },
      {
        path: 'initial-booking',
        loadChildren: () => import('./features/initial-booking/initial-booking.module').then((m) => m.InitialBookingModule),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
        data: { feature: 'initial-booking' }, // Used to handle feature specific logic in some components. e.g. single-passenger-form
      },
      {
        path: 'ticket-details',
        loadComponent: () => import('./features/ticket-details/ticket-detail/ticket-detail.component').then((m) => m.TicketDetailComponent),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard, TicketDetailsGuard],
      },
    ],
  },

  {
    path: 'reservation',
    component: NoCommonHeaderLayoutComponent,
    children: [
      {
        path: ':confcode/seatmap',
        loadChildren: () => import('./features/seat-map/seat-map.routes').then((m) => m.seatMapRoutes),
        canActivateChild: [SeatMapGuard],
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/seatmapv2',
        loadChildren: () => import('./features/reservation/reservation-seat-map/reservation-seat-map.routes').then((m) => m.routes),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/summary',
        canActivateChild: [PaymentDueGuard],
        loadChildren: () => import('./features/summary/summary.module').then((m) => m.SummaryModule),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/payment',
        canActivateChild: [PaymentGuard],
        loadChildren: () => import('./features/payment/payment.routes').then((m) => m.paymentRoutes),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/waivers',
        canActivateChild: [PaymentDueGuard],
        loadChildren: () => import('./features/waivers/waivers.routes').then((m) => m.waiversRoutes),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/involuntary-change/:involuntaryChangeType',
        canActivateChild: [InvoluntaryChangeGuard],
        loadChildren: () => import('./features/involuntary-change/involuntary-change.module').then((m) => m.InvoluntaryChangeModule),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/rebook',
        canActivateChild: [InvoluntaryChangeGuard],
        loadChildren: () => import('./features/voluntary-change/voluntary-change.routes').then((m) => m.routes),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/divide',
        loadChildren: () => import('./features/divide/divide.routes').then((m) => m.routes),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/same-day-confirm',
        canActivateChild: [InvoluntaryChangeGuard],
        loadChildren: () => import('./features/same-day-confirm/same-day-confirm.module').then((m) => m.SameDayConfirmModule),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/batch-change-coupon-status',
        loadComponent: () =>
          import('./features/dashboard-tickets/batch-change-coupon-status/batch-change-coupon-status.component').then(
            (m) => m.BatchChangeCouponStatusComponent
          ),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/change-coupon-status/:ticketNumber/:couponNumber',
        loadComponent: () =>
          import('./features/dashboard-tickets/change-coupon-status/change-coupon-status.component').then(
            (m) => m.ChangeCouponStatusComponent
          ),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/ssr',
        loadChildren: () => import('./features/ssr/ssr.routes').then((m) => m.routes),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/tcp',
        loadComponent: () => import('./features/tcp/tcp/tcp.component').then((m) => m.TcpComponent),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/standalone-fees',
        loadChildren: () => import('./features/standalone-fees/standalone-fees.module').then((m) => m.StandaloneFeesModule),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/cancel',
        loadChildren: () => import('./features/cancel/cancel.routes').then((m) => m.reservationCancelRoutes),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
      {
        path: ':confcode/resend-email',
        loadChildren: () => import('./features/dashboard-billing/resend-email/resend-email.routes').then((m) => m.resendEmailRoutes),
        canActivate: [BrowserGuard, AuthGuard, LoggedInUserGuard],
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      onSameUrlNavigation: 'reload',
      preloadingStrategy: PreloadAllModules,
    }),
    UXModule,
    CommonModule,
    StoreRouterConnectingModule.forRoot({
      // Change dispatch timing: dispatched after guards and resolvers successfully ran and the new route will be activated
      navigationActionTiming: NavigationActionTiming.PostActivation,
      // Built in ngrx serializer - ignores paramMap, queryParamMap, and component
      routerState: RouterState.Minimal,
    }),
  ],
  exports: [RouterModule],
  declarations: [AuthComponent],
})
export class AppRoutingModule {
  constructor(
    private readonly router: Router,
    private readonly store: Store<RootState>,
    private readonly destroyRef: DestroyRef,
    private readonly ngZone: NgZone
  ) {
    this.runRum((dtrum) => dtrum.enableManualPageDetection());

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const group = this.getRouteGroup(this.router.routerState.snapshot.root);
        this.runRum((dtrum) => dtrum.setPage({ name: group, group }));
      });

    this.store
      .select(getUser)
      .pipe(
        distinctUntilChanged(
          (previousUser, currentUser) =>
            previousUser?.username === currentUser?.username && previousUser?.sabreAAA === currentUser?.sabreAAA
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((user) => {
        if (!user?.username) {
          return;
        }
        this.runRum((dtrum) => {
          dtrum.identifyUser(user.username);
          if (user.sabreAAA) {
            dtrum.sendSessionProperties(undefined, undefined, { 'session_properties.aaa_city': user.sabreAAA });
          }
        });
      });
  }

  // Agent timers/beacons must not trigger Angular change detection.
  private runRum(action: (dtrum: DynatraceRumApi) => void): void {
    const dtrum = (window as Window & { dtrum?: DynatraceRumApi }).dtrum;
    if (dtrum) {
      this.ngZone.runOutsideAngular(() => action(dtrum));
    }
  }

  private getRouteGroup(snapshot: ActivatedRouteSnapshot): string {
    const routePaths: string[] = [];
    let currentSnapshot: ActivatedRouteSnapshot | null = snapshot;

    while (currentSnapshot) {
      const routePath = currentSnapshot.routeConfig?.path;
      if (routePath && routePath !== '**') {
        routePaths.unshift(routePath);
      }
      currentSnapshot = currentSnapshot.firstChild;
    }

    return `/${routePaths.join('/')}`;
  }

  static forRoot(): ModuleWithProviders<AppRoutingModule> {
    return {
      ngModule: AppRoutingModule,
      providers: [
        ReservationGuard,
        CanDeactivateGuard,
        BrowserGuard,
        LoggedInUserGuard,
        AuthGuard,
        {
          provide: HTTP_INTERCEPTORS,
          useClass: ApiInterceptor,
          multi: true,
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: EventInterceptor,
          multi: true,
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: AuthInterceptor,
          multi: true,
        },
      ],
    };
  }
}
